import { Dialog, Button } from "@/components/ui";
import React, { useEffect, useCallback } from "react";
import { SocialNetworksId, WebComponents, WebSettings, Post as DtoPost } from "@/interfaces";
import { Player, SocialMentionData, PostStatsResponse, PostDetails } from "@/services/bedrock";
import { TagIcon } from "lucide-react";
import { PostServices, PlayersServices } from "@/services/bedrock";
import { PostServices as LegacyPostServices } from "@/services";
import { useAccountSettings, useInterval, useSocialCoachSession } from "@/utils";
import {
  PostSummary,
  UpdateMentionsSection,
  LoadingState,
  ErrorState,
  SuccessState,
  PostDetailsActions,
} from "./components";
import { flatMap } from "lodash";
import { ReschedulePostsModal } from "@/containers/CalendarPage/components/EventModal/ReschedulePostsModal";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  postId: number;
  socialNetworkId?: SocialNetworksId;
  open: boolean;
  onClose: () => void;
}

export function PostDetailsDialog({ postId, socialNetworkId, open, onClose }: Props) {
  const queryClient = useQueryClient();

  const [post, setPost] = React.useState<PostDetails>();
  const [view, setView] = React.useState<"review" | "mentions" | "success">("review");
  const [player, setPlayer] = React.useState<Player | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeletingAll, setIsDeletingAll] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = React.useState<string | null>(null);
  const [mentions, setMentions] = React.useState<SocialMentionData[]>([]);
  const [activeNetworkTab, setActiveNetworkTab] = React.useState<string>("");
  const [pollingSocialPosts, setPollingSocialPosts] = React.useState<Record<number, string | null>>({});
  const [postStats, setPostStats] = React.useState<PostStatsResponse>();
  const [showRescheduleModal, setShowRescheduleModal] = React.useState(false);

  const { isPlayer } = useSocialCoachSession();
  const { hasWritePermission, hasAccountSetting } = useAccountSettings();
  const withWritePermission = isPlayer || hasWritePermission(WebComponents.CALENDAR);
  const hasMentionsFeature = hasAccountSetting(WebSettings.MENTIONS);

  const loadData = useCallback(async () => {
    if (!postId) return;

    setIsLoading(true);
    setError(null);
    try {
      const p = await PostServices.getPost(postId);
      setPost(p);

      setMentions(flatMap(p.postSocialNetworks.map(n => n.mentions || [])));
      const transitPosts = p.postSocialNetworks.filter(sn => sn.status === "IN_TRANSIT");
      if (transitPosts.length > 0) {
        setPollingSocialPosts(prev => {
          const next = { ...prev };
          transitPosts.forEach(sn => {
            if (!(sn.id in next)) {
              next[sn.id] = sn.postedTimestamp || null;
            }
          });
          return next;
        });
      }
    } catch (err) {
      console.error("Failed to load post details:", err);
      setError("Failed to load post details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [postId, setIsLoading, setError, setPost, setPollingSocialPosts, setMentions]);

  useEffect(() => {
    if (post && activeNetworkTab.length === 0) {
      setActiveNetworkTab(socialNetworkId || post.postSocialNetworks[0]?.socialNetworkId || "");
    }
  }, [post, activeNetworkTab, socialNetworkId]);

  useEffect(() => {
    if (open && postId) {
      loadData().catch(console.error);
    }
  }, [open, postId, loadData]);

  useEffect(() => {
    if (post?.playerId) {
      PlayersServices.getPlayer(post.playerId).then(setPlayer);
    }
  }, [post?.playerId]);

  const isPolling = Object.keys(pollingSocialPosts).length > 0;

  useInterval(
    () => {
      if (open && postId && isPolling) {
        PostServices.getPost(postId).then(p => {
          setPost(p);

          setPollingSocialPosts(prev => {
            const next = { ...prev };
            let changed = false;

            Object.entries(next).forEach(([idStr, initialTimestamp]) => {
              const id = parseInt(idStr);
              const sn = p.postSocialNetworks.find(s => s.id === id);
              if (sn) {
                const isNotTransit = sn.status !== "IN_TRANSIT";
                const timestampChanged = (sn.postedTimestamp || null) !== (initialTimestamp || null);

                if (isNotTransit && timestampChanged) {
                  delete next[id];
                  changed = true;
                }
              } else {
                delete next[id];
                changed = true;
              }
            });

            return changed ? next : prev;
          });
        });
      }
    },
    isPolling ? 5000 : undefined
  );

  const requestPostStats = async () => {
    if (postId) {
      try {
        const stats = await PostServices.getPostStats(postId);
        setPostStats(stats);
      } catch (e) {
        console.error("Failed to fetch post stats", e);
      }
    }
  };

  useEffect(() => {
    if (open && postId) {
      requestPostStats().catch(console.error);
    }
  }, [open, postId]);

  const handleStartPolling = useCallback(
    (socialPostId: number, postedTimestamp: string | null) => {
      setPollingSocialPosts(prev => ({
        ...prev,
        [socialPostId]: postedTimestamp,
      }));
    },
    [setPollingSocialPosts]
  );

  const handleAddMention = (mention: SocialMentionData) => {
    setMentions(prev => {
      const existingIndex = prev.findIndex(
        m => m.socialNetworkId === mention.socialNetworkId && m.externalId === mention.externalId
      );

      if (existingIndex >= 0) return prev;
      return [...prev, mention];
    });
  };

  const handleRemoveMention = (mention: SocialMentionData) => {
    setMentions(prev =>
      prev.filter(m => m.socialNetworkId !== mention.socialNetworkId || m.externalId !== mention.externalId)
    );
  };

  const handleSaveMentions = async () => {
    if (!post) return;
    setIsSaving(true);
    setError(null);
    try {
      const mentionsByNetwork = mentions.reduce((acc, m) => {
        if (!acc[m.socialNetworkId]) acc[m.socialNetworkId] = [];
        acc[m.socialNetworkId].push(m);
        return acc;
      }, {} as Record<string, SocialMentionData[]>);

      await Promise.all(
        post.postSocialNetworks.map(sn =>
          PostServices.updateSocialPost(postId, sn.id, {
            updateType: "UPDATE_MENTIONS",
            mentions: mentionsByNetwork[sn.socialNetworkId] || [],
          })
        )
      );

      setSaveSuccessMessage("Your mentions have been updated successfully.");
      setView("success");
    } catch (err) {
      console.error("Failed to update mentions:", err);
      setError("Failed to update mentions. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToDetails = () => {
    setSaveSuccessMessage(null);
    setView("review");
    loadData();
  };

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    LegacyPostServices.delete(postId)
      .then(() => {
        toast.success("Post has been deleted from schedule.");
        queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
        onClose();
      })
      .catch(e => {
        console.error("Failed to delete post", e);
        setError("Failed to delete post. Please try again. If the issue persists, please contact support.");
      })
      .finally(() => setIsDeletingAll(false));
  };

  const getMappedPost = (): DtoPost | null => {
    if (!post) return null;
    return {
      postId: post.id,
      playerId: post.playerId,
      promptId: post.promptId,
      message: post.message || "",
      mediaGroup:
        post.sourceType === "PROMPT" || post.sourceType === "CAMPAIGN"
          ? { mediaGroupId: post.sourceId, type: "MIX", media: post.media as any }
          : { type: "MIX", media: post.media as any },
      socialNetworks: post.postSocialNetworks.map(sn => ({
        postSocialNetworkId: sn.id,
        socialNetworkId: sn.socialNetworkId,
        status: sn.status as any,
        caption: sn.postedCaption,
        postedTimestamp: sn.postedTimestamp || "",
        externalPostUrl: sn.externalPostUrl,
      })),
      keyphrases: post.keyphrases,
      coachPostReviewStatus: post.reviewStatus,
      coachPostReviewStatusUpdatedDate: post.reviewStatusUpdatedDate || "",
      scheduledStatus: post.scheduledStatus as any,
      scheduledDatetimeUtc: post.scheduledDatetimeUtc,
    } as DtoPost;
  };

  const renderContent = () => {
    if (error && view !== "mentions") {
      return <ErrorState error={error} onRetry={loadData} />;
    }

    if (isLoading || isSaving || !post || !player) {
      return <LoadingState />;
    }

    if (view === "success") {
      return <SuccessState message={saveSuccessMessage || ""} onBack={handleBackToDetails} />;
    }

    if (view === "review") {
      return (
        <PostSummary
          post={post}
          player={player}
          withWritePermission={withWritePermission}
          activeNetworkTab={activeNetworkTab}
          setActiveNetworkTab={setActiveNetworkTab}
          postStats={postStats}
          pollingSocialPosts={pollingSocialPosts}
          onStartPolling={handleStartPolling}
          handleReload={loadData}
        />
      );
    }

    return (
      <UpdateMentionsSection
        post={post}
        player={player}
        mentions={mentions}
        onAddMention={handleAddMention}
        onRemoveMention={handleRemoveMention}
      />
    );
  };

  const mappedPost = getMappedPost();

  return (
    <>
      <Dialog
        open={open && !showRescheduleModal}
        onOpenChange={o => {
          if (!o) onClose();
        }}
        size={"2xl"}
      >
        <Dialog.Header>
          <Dialog.Title>
            {view === "review" ? "Post Details" : view === "mentions" ? "Update Mentions" : "Success"}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="max-h-[85vh] overflow-hidden outline-none" tabIndex={-1}>
          {renderContent()}
        </Dialog.Content>
        <Dialog.Footer>
          <div className="flex justify-end items-center w-full">
            <PostDetailsActions
              view={view}
              post={post}
              withWritePermission={withWritePermission}
              hasMentionsFeature={hasMentionsFeature}
              mentionsCount={mentions.length}
              isSaving={isSaving}
              isDeletingAll={isDeletingAll}
              onClose={onClose}
              onSetView={setView}
              onReschedule={() => setShowRescheduleModal(true)}
              onDeleteAll={handleDeleteAll}
              onSaveMentions={handleSaveMentions}
            />
          </div>
        </Dialog.Footer>
      </Dialog>
      {showRescheduleModal && post && mappedPost && (
        <ReschedulePostsModal
          open={showRescheduleModal}
          posts={[mappedPost]}
          currentScheduledDateTime={post.scheduledDatetimeUtc ? new Date(post.scheduledDatetimeUtc) : new Date()}
          onClose={() => {
            setShowRescheduleModal(false);
            onClose();
          }}
          onPostsRescheduled={() => {
            setShowRescheduleModal(false);
            onClose();
          }}
        />
      )}
    </>
  );
}
